import { ILookupModel, IMultiSelectOption } from '../../interfaces/ILookup';
import { Stack, Table } from 'react-bootstrap';
import { cloneDeep, isEmpty } from 'lodash';
import { useEffect, useState } from 'react';

import Authorization from '../../stores/Authorization';
import { CaseFilterType } from '../../utilities/Constants';
import CheckmateNSelect from '../shared/CheckmateNSelect';
import { DefaultCaseSearchModel } from '../../pages/case/CaseListPageWrapper';
import { ICaseSearchModel } from '../../interfaces/ICase';
import { IUserModel } from '../../interfaces/IUser';
import { LoadingButton } from '../../shared/buttons/LoadingButton';
import { showErrorToast } from '../../shared/ToastUtils';
import { useCaseManagersInZone } from '../../shared/react-query-hooks/useCaseManagersInZone';
import { useGetCaseStatuses } from '../quad/same-case-easy-update/useGetCaseStatuses';
import { useGetDiagnoses } from '../../shared/react-query-hooks/useGetDiagnoses';
import { useGetPlaintiffsFirms } from '../../shared/react-query-hooks/useGetPlaintiffsFirms';
import { useGetQScores } from '../../shared/react-query-hooks/useGetQScores';
import { useGetUsStatesLookup } from '../../shared/react-query-hooks/useUsStatesLookup';
import { useGetZoneJurisdictions } from '../../shared/react-query-hooks/useGetZoneJurisdictions';
import { useZoneContext } from '../../contexts/useZoneContext';

const lookupToOptionMapper = (lookupItem: ILookupModel) =>
    ({
        label: lookupItem.displayName!,
        value: lookupItem.name!,
        id: lookupItem.id,
        guid: lookupItem.guid,
    }) as IMultiSelectOption;

export interface ICaseListSearchFiltersProps {
    appliedSearchModel: ICaseSearchModel;
    isSearching: boolean;
    onApplyFilters: (filters: ICaseSearchModel) => void;
}

export function CaseListSearchFilters(props: ICaseListSearchFiltersProps) {
    const caseStatusTypesApi = useGetCaseStatuses();
    const jurisdictionsApi = useGetZoneJurisdictions();
    const diagnosesApi = useGetDiagnoses();
    const statesApi = useGetUsStatesLookup();
    const plaintiffsFirmsApi = useGetPlaintiffsFirms();
    const caseManagersApi = useCaseManagersInZone();
    const sqScoresApi = useGetQScores();
    const { zoneGuid } = useZoneContext();

    const [pendingSearchModel, setPendingSearchModel] = useState<ICaseSearchModel>(
        props.appliedSearchModel
    );

    const validateFilters = (): boolean => {
        const hasAtLeastOneFilter = [
            pendingSearchModel.statusIds,
            pendingSearchModel.subStatusIds,
            pendingSearchModel.searchText,
            pendingSearchModel.filters,
        ].some((value) => !isEmpty(value));

        if (!hasAtLeastOneFilter) {
            showErrorToast('Enter at least 1 filter or Search by Name or Number');
        }

        return hasAtLeastOneFilter;
    };

    const handleApplyFiltersClick = () => {
        if (!validateFilters()) {
            return;
        }

        props.onApplyFilters(pendingSearchModel);
    };

    const handleClearFiltersClick = () => {
        setPendingSearchModel({
            ...DefaultCaseSearchModel,
            filters: [],
            searchText: props.appliedSearchModel.searchText,
            statusIds: [],
            subStatusIds: [],
        });
    };

    const handleStatusChange = (selectedOptions: IMultiSelectOption[]) => {
        const pendingSearchModelCopy: ICaseSearchModel = cloneDeep(pendingSearchModel);

        pendingSearchModelCopy.statusIds = [];
        pendingSearchModelCopy.subStatusIds = [];

        selectedOptions.forEach(({ value }) => {
            const match = caseStatusTypesApi.data?.find((x) => x.name === value);

            if (match) {
                pendingSearchModelCopy.statusIds?.push(match.status.id);

                if (match.subStatus) {
                    pendingSearchModelCopy.subStatusIds?.push(match.subStatus.id);
                }
            }
        });

        setPendingSearchModel(pendingSearchModelCopy);
    };

    const handleDiagnosisChange = (selectedOptions: IMultiSelectOption[]) => {
        const filtersCopy = cloneDeep(pendingSearchModel.filters) ?? [];
        const cleanFilters = filtersCopy.filter((x) => x.typeId !== CaseFilterType.Diagnosis.Value);

        selectedOptions.forEach((option: IMultiSelectOption) => {
            const match = diagnosesApi.data?.find((x) => x.name === option.label);

            if (match) {
                cleanFilters.push({
                    typeId: CaseFilterType.Diagnosis.Value,
                    text: match.name?.toString() ?? '',
                });
            }
        });

        setPendingSearchModel({ ...pendingSearchModel, filters: cleanFilters });
    };

    const handleJurisdictionsChange = (selectedOptions: IMultiSelectOption[]) => {
        const filtersCopy = cloneDeep(pendingSearchModel.filters) ?? [];
        const cleanFilters = filtersCopy.filter(
            (x) => x.typeId !== CaseFilterType.Jurisdiction.Value
        );

        selectedOptions.forEach((option: IMultiSelectOption) => {
            const match = jurisdictionsApi.data?.find((x) => x.name === option.label);

            if (match) {
                cleanFilters.push({
                    typeId: CaseFilterType.Jurisdiction.Value,
                    text: match.name?.toString() ?? '',
                });
            }
        });

        setPendingSearchModel({ ...pendingSearchModel, filters: cleanFilters });
    };

    const handleStatesChange = (selectedOptions: IMultiSelectOption[]) => {
        const filtersCopy = cloneDeep(pendingSearchModel.filters) ?? [];
        const cleanFilters = filtersCopy.filter((x) => x.typeId !== CaseFilterType.State.Value);

        selectedOptions.forEach((option: any) => {
            const match = statesApi.data?.find((x) => x.name === option.value);

            if (match) {
                cleanFilters.push({
                    typeId: CaseFilterType.State.Value,
                    text: match.name?.toString() ?? '',
                });
            }
        });

        setPendingSearchModel({ ...pendingSearchModel, filters: cleanFilters });
    };

    const handlePlaintiffsFirmsChange = (selectedOptions: IMultiSelectOption[]) => {
        const filtersCopy = cloneDeep(pendingSearchModel.filters) ?? [];
        const cleanFilters = filtersCopy.filter(
            (x) => x.typeId !== CaseFilterType.PlaintiffsFirm.Value
        );

        selectedOptions.forEach((option: IMultiSelectOption) => {
            const match = plaintiffsFirmsApi.data?.find((x) => x.name === option.label);

            if (match) {
                cleanFilters.push({
                    typeId: CaseFilterType.PlaintiffsFirm.Value,
                    text: match.name?.toString() ?? '',
                });
            }
        });

        setPendingSearchModel({ ...pendingSearchModel, filters: cleanFilters });
    };

    const handleCaseManagersChange = (selectedOptions: IMultiSelectOption[]) => {
        const filtersCopy = cloneDeep(pendingSearchModel.filters) ?? [];
        const cleanFilters = filtersCopy.filter(
            (x) => x.typeId !== CaseFilterType.CaseManager.Value
        );

        selectedOptions.forEach((option: IMultiSelectOption) => {
            const match = caseManagersApi.data?.find((x) => x.guid === option.guid);

            if (match) {
                cleanFilters.push({
                    typeId: CaseFilterType.CaseManager.Value,
                    text: match.guid?.toString() ?? '',
                });
            }
        });

        setPendingSearchModel({ ...pendingSearchModel, filters: cleanFilters });
    };

    const handleSqScoresChange = (selectedOptions: IMultiSelectOption[]) => {
        const filtersCopy = cloneDeep(pendingSearchModel.filters) ?? [];
        const cleanFilters = filtersCopy.filter((x) => x.typeId !== CaseFilterType.QScore.Value);

        selectedOptions.forEach((option: IMultiSelectOption) => {
            const match = sqScoresApi.data?.find((x) => x.guid === option.guid);

            if (match) {
                cleanFilters.push({
                    typeId: CaseFilterType.QScore.Value,
                    text: match.name?.toString() ?? '',
                });
            }
        });

        setPendingSearchModel({ ...pendingSearchModel, filters: cleanFilters });
    };

    useEffect(() => {
        setPendingSearchModel(props.appliedSearchModel);
    }, [props.appliedSearchModel]);

    const statusOptions =
        caseStatusTypesApi.data?.map((status) => ({
            label: status.name!,
            value: status.name!,
            id: status.id,
        })) ?? [];
    const selectedStatuses =
        caseStatusTypesApi?.data?.filter(
            (caseStatus) =>
                pendingSearchModel.statusIds?.includes(caseStatus.status.id) &&
                (caseStatus.subStatus == null ||
                    pendingSearchModel.subStatusIds?.includes(caseStatus.subStatus.id))
        ) ?? [];
    const selectedStatusOptions = statusOptions.filter((opt) =>
        selectedStatuses.map((x) => x.id).includes(opt.id)
    );

    const diagnosisOptions = diagnosesApi.data?.map(lookupToOptionMapper) ?? [];
    const selectedDiagnoses =
        pendingSearchModel.filters?.filter(
            (filter) => filter.typeId === CaseFilterType.Diagnosis.Value
        ) ?? [];
    const selectedDiagnosisOptions = diagnosisOptions.filter((opt) =>
        selectedDiagnoses.map((x) => x.text).includes(opt.label.toString())
    );

    const stateOptions = statesApi.data?.map(lookupToOptionMapper) ?? [];
    const selectedStates =
        pendingSearchModel.filters?.filter(
            (filter) => filter.typeId === CaseFilterType.State.Value
        ) ?? [];
    const selectedStateOptions = stateOptions.filter((opt) =>
        selectedStates.map((x) => x.text).includes(opt.value.toString())
    );

    const plaintiffsFirmOptions = plaintiffsFirmsApi.data?.map(lookupToOptionMapper) ?? [];
    const selectedPlaintiffsFirms =
        pendingSearchModel.filters?.filter(
            (filter) => filter.typeId === CaseFilterType.PlaintiffsFirm.Value
        ) ?? [];
    const selectedPlaintiffsFirmOptions = plaintiffsFirmOptions.filter((opt) =>
        selectedPlaintiffsFirms.map((x) => x.text).includes(opt.label)
    );

    const jurisdictionOptions = jurisdictionsApi.data?.map(lookupToOptionMapper) ?? [];
    const selectedJurisdictions =
        pendingSearchModel.filters?.filter(
            (filter) => filter.typeId === CaseFilterType.Jurisdiction.Value
        ) ?? [];
    const selectedJurisdictionOptions = jurisdictionOptions.filter((opt) =>
        selectedJurisdictions.map((x) => x.text).includes(opt.label)
    );

    const caseManagerOptions =
        caseManagersApi.data?.map((user: IUserModel) => ({
            label: user.profile!.firstName + ' ' + user.profile!.lastName,
            value: user.guid! + (Authorization.isLocalScope(user, zoneGuid) ? ' (Local)' : ''),
            id: CaseFilterType.CaseManager.Value,
            guid: user.guid,
        })) ?? [];
    const selectedCaseManagers =
        pendingSearchModel.filters?.filter(
            (filter) => filter.typeId === CaseFilterType.CaseManager.Value
        ) ?? [];
    const selectedCaseManagerOptions = caseManagerOptions.filter((opt) =>
        selectedCaseManagers.map((x) => x.text).includes(opt.guid?.toString() ?? '')
    );

    const sqScoreOptions = sqScoresApi.data?.map(lookupToOptionMapper) ?? [];
    const selectedSqScores =
        pendingSearchModel.filters?.filter(
            (filter) => filter.typeId === CaseFilterType.QScore.Value
        ) ?? [];
    const selectedSqScoreOptions = sqScoreOptions.filter((opt) =>
        selectedSqScores.map((x) => x.text).includes(opt.value.toString())
    );

    return (
        <Stack style={{ justifyContent: 'space-between', height: '100%' }}>
            <div style={{ flexGrow: 1 }}>
                <Table borderless className="mt-1">
                    <tbody>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td style={{ width: 125 }} className="nowrap">
                                Status
                            </td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handleStatusChange}
                                    options={statusOptions}
                                    value={selectedStatusOptions}
                                    disabled={caseStatusTypesApi.isFetching}
                                />
                            </td>
                        </tr>

                        <tr style={{ verticalAlign: 'middle' }}>
                            <td style={{ width: 125 }} className="nowrap">
                                Disease
                            </td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handleDiagnosisChange}
                                    options={diagnosisOptions}
                                    value={selectedDiagnosisOptions}
                                    disabled={diagnosesApi.isFetching}
                                />
                            </td>
                        </tr>

                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">Jurisdiction</td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handleJurisdictionsChange}
                                    options={jurisdictionOptions}
                                    value={selectedJurisdictionOptions}
                                    disabled={jurisdictionsApi.isFetching}
                                />
                            </td>
                        </tr>

                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">State</td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handleStatesChange}
                                    options={stateOptions}
                                    value={selectedStateOptions}
                                    disabled={statesApi.isFetching}
                                />
                            </td>
                        </tr>

                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">Plaintiffs Firm</td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handlePlaintiffsFirmsChange}
                                    options={plaintiffsFirmOptions}
                                    value={selectedPlaintiffsFirmOptions}
                                    disabled={plaintiffsFirmsApi.isFetching}
                                />
                            </td>
                        </tr>

                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">A1/A2</td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handleCaseManagersChange}
                                    options={caseManagerOptions}
                                    value={selectedCaseManagerOptions}
                                    disabled={caseManagersApi.isFetching}
                                />
                            </td>
                        </tr>

                        <tr style={{ verticalAlign: 'middle' }}>
                            <td className="nowrap">SQ Score</td>
                            <td>
                                <CheckmateNSelect
                                    onChange={handleSqScoresChange}
                                    options={sqScoreOptions}
                                    value={selectedSqScoreOptions}
                                    disabled={sqScoresApi.isFetching}
                                />
                            </td>
                        </tr>
                    </tbody>
                </Table>
            </div>
            <div style={{ flexShrink: 1, paddingTop: '1.5em', borderTop: '1px solid lightgray' }}>
                <LoadingButton
                    buttonCssClass="btn btn-black"
                    onClick={handleApplyFiltersClick}
                    text="Apply Filters"
                    loadingText="Searching..."
                    loading={props.isSearching}
                />
                <button
                    className="btn btn-default"
                    onClick={handleClearFiltersClick}
                    disabled={props.isSearching}
                >
                    Clear Filters
                </button>
            </div>
        </Stack>
    );
}
